import React, { useEffect, useMemo, useState } from "react";
import noImgPlaceholder from "../../Assets/no-img-placeholder.png";
import {
  Box,
  Grid,
  Typography,
  Card,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  TablePagination,
  Stack,
  LinearProgress,
  Paper,
} from "@mui/material";
import {
  Search,
  Star,
  LocationOn,
  TrendingUp,
  InfoOutlined,
  ArrowForward,
  Home,
  Business,
  Apartment,
  Hive,
  SwapHoriz,
  CheckCircle,
  Diamond,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import InvestorMyInvestmentCard from "./InvestorMyInvestmentCard";
import { useGetInvestorAssetsQuery, useGetMyInvestmentsQuery } from "../../redux/api/authApi";
import { cleanLocation } from "../../utils/location";

const ALL_CATEGORY = "all";

const CATEGORY_FILTERS = [
  { id: ALL_CATEGORY, label: "All", Icon: null },
  { id: "Real Estate", label: "Real Estate", Icon: Apartment },
  { id: "Commodities", label: "Commodities", Icon: Hive },
  { id: "Luxury Goods", label: "Luxury Goods", Icon: Diamond },
  // { id: "Financial Products", label: "Financial Products", Icon: ShowChart },
  // { id: "Supply Chain", label: "Supply Chain", Icon: Inventory2 },
  // { id: "Healthcare", label: "Healthcare", Icon: LocalHospital },
  // { id: "Arts", label: "Arts", Icon: Palette },
  // { id: "Renewable Energy", label: "Renewable Energy", Icon: ElectricBolt },
];

function formatMoney(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(x);
}

function formatTokens(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "0";
  return new Intl.NumberFormat("en-US").format(Math.round(x));
}

function PropertyTypeIcon({ propertyType }) {
  const t = String(propertyType || "").toLowerCase();
  if (t.includes("commercial")) return <Business sx={{ fontSize: 16 }} />;
  if (t.includes("residential")) return <Home sx={{ fontSize: 16 }} />;
  return <Apartment sx={{ fontSize: 16 }} />;
}

function AssetCard({ asset }) {
  const navigate = useNavigate();
  const totalSupply = Number(asset.totalSupply) || 0;
  const tokensSold = Number(asset.tokensSold) || 0;
  const available = Math.max(0, totalSupply - tokensSold);
  const soldPct = totalSupply > 0 ? Math.min(100, (tokensSold / totalSupply) * 100) : 0;
  const isActive = String(asset.status || "").toLowerCase() === "active";

  return (
    <Card
      sx={{
        borderRadius: 1.5,
        overflow: "hidden",
        boxShadow: (theme) => theme.palette.shadow.statCard,
        border: (theme) => `1px solid ${theme.palette.border.light}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 200,
          bgcolor: "border.light",
          backgroundImage: `url(${asset.mainImage || noImgPlaceholder})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Stack direction="row" spacing={1} sx={{ position: "absolute", top: 12, left: 12, flexWrap: "wrap", gap: 0.5 }}>
          <Chip
            label={asset.status || "Active"}
            size="small"
            sx={{
              bgcolor: (theme) =>
                isActive
                  ? theme.palette.chips.success.text
                  : theme.palette.text.secondary,
              color: "common.white",
              fontWeight: 700,
              fontSize: 11,
              height: 26,
            }}
          />
          {/* <Chip
            icon={
              <Verified
                sx={{
                  fontSize: "16px !important",
                  color: (theme) =>
                    `${theme.palette.chips.success.text} !important`,
                }}
              />
            }
            label="DLD Verified"
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.95)",
              fontWeight: 600,
              fontSize: 11,
              height: 26,
              border: "1px solid rgba(0,0,0,0.06)",
              "& .MuiChip-icon": { ml: "6px" },
            }}
          /> */}
        </Stack>
        {asset.propertyType ? (
          <Chip
            icon={<PropertyTypeIcon propertyType={asset.propertyType} />}
            label={asset.propertyType}
            size="small"
            sx={{
              position: "absolute",
              bottom: 12,
              left: 12,
              bgcolor: "rgba(255,255,255,0.95)",
              fontWeight: 600,
              fontSize: 12,
              border: "1px solid rgba(0,0,0,0.06)",
              "& .MuiChip-icon": { ml: "6px" },
            }}
          />
        ) : null}
      </Box>

      <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography fontWeight={800} fontSize={18} sx={{ color: "text.primary", mb: 0.75, lineHeight: 1.3 }}>
          {asset.assetName || "—"}
        </Typography>
        {cleanLocation(asset.location) ? (
          <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ mb: 2 }}>
            <LocationOn sx={{ fontSize: 18, color: "text.faint", mt: 0.15 }} />
            <Typography variant="body2" sx={{ color: "text.muted", lineHeight: 1.45 }}>
              {cleanLocation(asset.location)}
            </Typography>
          </Stack>
        ) : null}

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid size={6}>
            <Box
              sx={{
                bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
                borderRadius: 1.25,
                p: 1.5,
                height: "100%",
              }}
            >
              <Typography variant="caption" sx={{ color: "text.muted" }} fontWeight={600} display="block" mb={0.5}>
                Expected ROI
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TrendingUp sx={{ fontSize: 18, color: (theme) => theme.palette.accent.teal.chipText }} />
                <Typography fontWeight={800} fontSize={18} sx={{ color: "text.primary" }}>
                  {asset.expectedROI != null ? `${asset.expectedROI}%` : "—"}
                </Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid size={6}>
            <Box
              sx={{
                bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
                borderRadius: 1.25,
                p: 1.5,
                height: "100%",
              }}
            >
              <Typography variant="caption" sx={{ color: "text.muted" }} fontWeight={600} display="block" mb={0.5}>
                Yield
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TrendingUp sx={{ fontSize: 18, color: (theme) => theme.palette.accent.teal.chipText }} />
                <Typography fontWeight={800} fontSize={18} sx={{ color: "text.primary" }}>
                  {asset.yield != null ? `${asset.yield}%` : "—"}
                </Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight={600} sx={{ color: "text.neutral" }}>
                Tokens sold
              </Typography>
              <InfoOutlined sx={{ fontSize: 16, color: "text.faint" }} />
            </Stack>
            <Typography variant="body2" fontWeight={700} sx={{ color: "text.primary" }}>
              {soldPct.toFixed(0)}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={soldPct}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: "border.light",
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                bgcolor: (theme) => theme.palette.accent.teal.chipText,
              },
            }}
          />
          <Typography variant="caption" sx={{ color: "text.muted", mt: 1, display: "block" }}>
            {formatTokens(available)} of {formatTokens(totalSupply)} tokens available
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid size={6}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "text.muted" }} fontWeight={600}>
                Minimum Investment
              </Typography>
              <InfoOutlined sx={{ fontSize: 14, color: "text.faint" }} />
            </Stack>
            <Typography fontWeight={800} fontSize={15} sx={{ color: "text.primary" }}>
              SAR {formatMoney(asset.minimumInvestment)}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "text.muted" }} fontWeight={600}>
                Price per Token
              </Typography>
              <InfoOutlined sx={{ fontSize: 14, color: "text.faint" }} />
            </Stack>
            <Typography fontWeight={800} fontSize={15} sx={{ color: "text.primary" }}>
              SAR {formatMoney(asset.pricePerToken)}
            </Typography>
          </Grid>
        </Grid>

        <Button
          fullWidth
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={() => asset?._id && navigate(`/investor/viewinvest/${asset._id}`)}
          sx={{
            mt: "auto",
            textTransform: "none",
            fontWeight: 700,
            py: 1.25,
            borderRadius: 1.25,
            color: "common.white",
            background: (theme) => theme.palette.gradients.teal,
            boxShadow: "none",
            "&:hover": {
              background: (theme) => theme.palette.gradients.tealHover,
              boxShadow: "none",
            },
          }}
        >
          View & Invest
        </Button>
      </Box>
    </Card>
  );
}

const InvestorViewAndInvest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewTab, setViewTab] = useState("discover");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryId, setCategoryId] = useState(ALL_CATEGORY);
  const [myCategoryId, setMyCategoryId] = useState(ALL_CATEGORY);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryId, limit]);

  useEffect(() => {
    if (viewTab === "my") setPage(1);
  }, [myCategoryId, viewTab]);

  useEffect(() => {
    if (location.state?.viewTab === "my") {
      setViewTab("my");
    }
  }, [location.pathname, location.state?.viewTab]);

  const queryArg = useMemo(
    () => ({
      search: debouncedSearch,
      status: "",
      category: categoryId === ALL_CATEGORY ? "" : categoryId,
      page,
      limit,
    }),
    [debouncedSearch, categoryId, page, limit]
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useGetInvestorAssetsQuery(queryArg, {
    skip: viewTab !== "discover",
  });

  const {
    data: myData,
    isLoading: myLoading,
    isFetching: myFetching,
    isError: myIsError,
    error: myError,
    refetch: myRefetch,
  } = useGetMyInvestmentsQuery(
    { page, limit },
    { skip: viewTab !== "my" }
  );

  const list = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.pagination && typeof data.pagination === "object" ? data.pagination : {};
  const total = Number(pagination.total ?? 0) || 0;

  const myList = useMemo(
    () => (Array.isArray(myData?.data) ? myData.data : []),
    [myData?.data]
  );
  const myPagination = myData?.pagination && typeof myData.pagination === "object" ? myData.pagination : {};
  const myTotal = Number(myPagination.total ?? 0) || 0;

  const filteredMyList = useMemo(() => {
    if (viewTab !== "my") return [];
    if (myCategoryId === ALL_CATEGORY) return myList;
    return myList.filter((row) => {
      const name = String(row?.asset?.assetCategory?.name || row?.asset?.category || "").trim();
      return name === myCategoryId;
    });
  }, [viewTab, myList, myCategoryId]);

  const showLoader = viewTab === "discover" && (isLoading || (isFetching && !list.length));
  const showMyLoader = viewTab === "my" && (myLoading || (myFetching && !myList.length));

  return (
    <Box>
      {/* Dark blue header banner */}
      {/* <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 1.5,
          mb: 3,
          bgcolor: (theme) => theme.palette.accent.navy.primary,
          backgroundImage: (theme) =>
            `linear-gradient(135deg, ${theme.palette.accent.navy.primary} 0%, ${theme.palette.accent.navy.darkest} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.25,
                bgcolor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ExploreOutlinedIcon sx={{ color: "common.white", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "common.white", letterSpacing: -0.5 }}
              >
                View &amp; Invest
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}
              >
                Discover tokenized assets across multiple categories
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={`${total || list.length} Available`}
            sx={{
              bgcolor: "rgba(255,255,255,0.1)",
              color: "common.white",
              fontWeight: 600,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          />
        </Stack>
      </Paper> */}

      {/* How Tokenized Investing Works info card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 1.5,
          mb: 3,
          bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <InfoOutlined
            sx={{
              fontSize: 20,
              color: (theme) => theme.palette.accent.teal.chipText,
            }}
          />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: "text.primary" }}
          >
            How Tokenized Investing Works
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: "text.muted", mb: 1.5 }}>
          Each asset is divided into blockchain tokens, allowing you to invest with as little as SAR 100. Own a fraction of high-value assets and earn proportional returns.
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          {[
            "VARA-compliant smart contracts",
            "Instant settlement on blockchain",
            "24/7 trading on secondary market",
          ].map((line) => (
            <Stack key={line} direction="row" alignItems="center" spacing={0.75}>
              <CheckCircle
                sx={{
                  fontSize: 16,
                  color: (theme) => theme.palette.chips.success.text,
                }}
              />
              <Typography variant="body2" sx={{ color: "text.primary", fontSize: 13 }}>
                {line}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Button
          variant={viewTab === "discover" ? "contained" : "outlined"}
          startIcon={<Star />}
          onClick={() => {
            setViewTab("discover");
            setPage(1);
          }}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 999,
            px: 2.5,
            py: 1,
            ...(viewTab === "discover"
              ? {
                  color: "common.white",
                  background: (theme) => theme.palette.gradients.teal,
                  "&:hover": {
                    background: (theme) => theme.palette.gradients.tealHover,
                  },
                }
              : {
                  borderColor: "border.light",
                  color: "text.muted",
                }),
          }}
        >
          Discover Assets
        </Button>
        <Button
          variant={viewTab === "my" ? "contained" : "outlined"}
          onClick={() => {
            setViewTab("my");
            setPage(1);
          }}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 999,
            px: 2.5,
            py: 1,
            ...(viewTab === "my"
              ? {
                  color: "common.white",
                  background: (theme) => theme.palette.gradients.teal,
                  "&:hover": {
                    background: (theme) => theme.palette.gradients.tealHover,
                  },
                }
              : {
                  borderColor: "border.light",
                  color: "text.muted",
                }),
          }}
        >
          My Investments
          {(myTotal > 0 || myList.length > 0) ? (
            <Chip
              component="span"
              label={myTotal || myList.length}
              size="small"
              sx={{
                ml: 1,
                height: 22,
                minWidth: 22,
                fontWeight: 800,
                fontSize: 12,
                bgcolor: viewTab === "my" ? "rgba(255,255,255,0.2)" : "#00A48E",
                color: "#fff",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          ) : null}
        </Button>
      </Stack>

      {viewTab === "my" ? (
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: 3,
            background: "linear-gradient(120deg, #f5f3ff 0%, #ede9fe 100%)",
            border: "1px solid #ddd6fe",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start" flex={1} minWidth={260}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e9d5ff",
                flexShrink: 0,
              }}
            >
              <SwapHoriz sx={{ color: "#7c3aed" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} color="#4c1d95" sx={{ mb: 0.5 }}>
                Ready to Trade Your Tokens?
              </Typography>
              <Typography variant="body2" color="#5b21b6">
                Access the secondary market to buy and sell tokens from your portfolio with real-time pricing and secure
                settlement.
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mt: 1.5 }}>
                {["Instant Settlement", "Real-time Pricing", "Secure Trading"].map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    sx={{
                      border: "1px solid #a78bfa",
                      bgcolor: "#fff",
                      color: "#6d28d9",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={() => navigate("/investor/secondary-market")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
              py: 1.25,
              borderRadius: 2,
              bgcolor: "#7c3aed",
              boxShadow: "none",
              "&:hover": { bgcolor: "#6d28d9", boxShadow: "none" },
            }}
          >
            Trade Your Tokens
          </Button>
        </Box>
      ) : null}

      {viewTab === "discover" ? (
        <>
          <TextField
            fullWidth
            size="small"
            placeholder="Search assets, properties, documents..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              mb: 3,
              maxWidth: 560,
              bgcolor: "background.paper",
              borderRadius: 1.25,
              "& .MuiOutlinedInput-root": { borderRadius: 1.25 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: "text.muted" }} />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap gap={1} sx={{ mb: 3 }}>
            <InfoOutlined sx={{ fontSize: 20, color: "text.muted" }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: "text.muted", mr: 1 }}>
              Filter by category:
            </Typography>
            {CATEGORY_FILTERS.map((c) => {
              const active = categoryId === c.id;
              const Icon = c.Icon;
              return (
                <Button
                  key={c.id}
                  onClick={() => setCategoryId(c.id)}
                  startIcon={Icon ? <Icon sx={{ fontSize: 18 }} /> : undefined}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: 999,
                    px: 2,
                    py: 1,
                    minHeight: 40,
                    border: "1px solid",
                    ...(active
                      ? {
                          color: "common.white",
                          borderColor: (theme) =>
                            theme.palette.accent.teal.chipText,
                          background: (theme) =>
                            theme.palette.gradients.teal,
                          "&:hover": {
                            background: (theme) =>
                              theme.palette.gradients.tealHover,
                          },
                        }
                      : {
                          color: "text.muted",
                          borderColor: "border.light",
                          bgcolor: "background.paper",
                          "&:hover": {
                            bgcolor: "background.subtle",
                          },
                        }),
                  }}
                >
                  {c.label}
                </Button>
              );
            })}
          </Stack>

          {isError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => refetch()}>
              {error?.data?.message || error?.error || "Failed to load assets."}
            </Alert>
          ) : null}

          {showLoader ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: (theme) => theme.palette.accent.teal.chipText }} />
            </Box>
          ) : list.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
              No assets found.
            </Typography>
          ) : (
            <>
              <Grid container spacing={3}>
                {list.map((asset) => (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={asset._id}>
                    <AssetCard asset={asset} />
                  </Grid>
                ))}
              </Grid>
              <TablePagination
                component="div"
                count={Math.max(total, list.length)}
                page={Math.max(0, page - 1)}
                onPageChange={(_, p) => setPage(p + 1)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[9, 18, 27]}
                labelRowsPerPage="Rows per page"
                sx={{
                  mt: 3,
                  bgcolor: "transparent",
                  ".MuiTablePagination-toolbar": { px: 0 },
                }}
              />
            </>
          )}
        </>
      ) : (
        <>
          {myIsError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => myRefetch()}>
              {myError?.data?.message || myError?.error || "Failed to load your investments."}
            </Alert>
          ) : null}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            gap={1}
            sx={{ mb: 3 }}
          >
            <InfoOutlined sx={{ fontSize: 20, color: "#64748b" }} />
            <Typography variant="body2" fontWeight={600} color="#475569" sx={{ mr: 1 }}>
              Filter your assets:
            </Typography>
            {CATEGORY_FILTERS.map((c) => {
              const active = myCategoryId === c.id;
              const Icon = c.Icon;
              return (
                <Button
                  key={c.id}
                  onClick={() => setMyCategoryId(c.id)}
                  startIcon={Icon ? <Icon sx={{ fontSize: 18 }} /> : undefined}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: 999,
                    px: 2,
                    py: 1,
                    minHeight: 40,
                    border: "1px solid",
                    ...(active
                      ? {
                          color: "common.white",
                          borderColor: (theme) =>
                            theme.palette.accent.teal.chipText,
                          background: (theme) =>
                            theme.palette.gradients.teal,
                          "&:hover": {
                            background: (theme) =>
                              theme.palette.gradients.tealHover,
                          },
                        }
                      : {
                          color: "text.muted",
                          borderColor: "border.light",
                          bgcolor: "background.paper",
                          "&:hover": {
                            bgcolor: "background.subtle",
                          },
                        }),
                  }}
                >
                  {c.label}
                </Button>
              );
            })}
          </Stack>

          {showMyLoader ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: (theme) => theme.palette.accent.teal.chipText }} />
            </Box>
          ) : myList.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1.5,
                p: 6,
                textAlign: "center",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                boxShadow: (theme) => theme.palette.shadow.statCard,
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", mb: 1 }}>
                No investments yet
              </Typography>
              <Typography sx={{ color: "text.muted" }}>
                Discover assets and invest to see your holdings here.
              </Typography>
            </Paper>
          ) : filteredMyList.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1.5,
                p: 6,
                textAlign: "center",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                boxShadow: (theme) => theme.palette.shadow.statCard,
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", mb: 1 }}>
                No assets in this category
              </Typography>
              <Typography sx={{ color: "text.muted" }}>
                Try selecting a different category or view all assets.
              </Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={3}>
                {filteredMyList.map((row, i) => (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={row?.asset?._id ? String(row.asset._id) : `my-${i}`}>
                    <InvestorMyInvestmentCard row={row} />
                  </Grid>
                ))}
              </Grid>
              <TablePagination
                component="div"
                count={Math.max(myTotal, myList.length)}
                page={Math.max(0, page - 1)}
                onPageChange={(_, p) => setPage(p + 1)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[9, 18, 27]}
                labelRowsPerPage="Rows per page"
                sx={{
                  mt: 3,
                  bgcolor: "transparent",
                  ".MuiTablePagination-toolbar": { px: 0 },
                }}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default InvestorViewAndInvest;
